// P4-N5B — C4 UI-B ADMIN GENERATE-INVOICE CUSTOMER-SCOPED
// NETWORK AUTHORIZATION HELPER R1 (Feb 2026)
//
// SEMANTIC PARITY REFERENCE:
//   The BEHAVIORAL contract of `deriveAuthorizedInvoiceNetworks`
//   in this file is byte-parity with the frozen C7 helper at
//   `frontend/src/pages/customer/CustomerInvoices.jsx` lines 28-51
//   (CustomerInvoices.jsx SHA-256
//   `4672c7e7366bbaf4e4fa46d8e6f96b2b30217ccb96324dd738600625e2f9fe98`).
//
// REUSE / EXTRACTION RULE:
//   Admin helper extraction created for reuse without altering
//   frozen C7. Future consolidation of C7 onto the shared helper
//   requires separate authorization. Neither CustomerInvoices.jsx
//   nor any other frozen slice may be edited to import from here
//   under the current authorization scope (P4-N5B C4 UI-B R1).
//
// SEMANTICS:
//   Derives the union of a customer's authorized canonical portal
//   networks from BOTH legacy `price_report_networks` (Esso/Pipeline
//   CAD/L) and newer `authorized_lanes` (per-lane {network,country}
//   for Petro-Pass). Compound lane IDs are canonicalized to the
//   canonical network label so the UI never surfaces `esso_us`,
//   `ta_petro`, `Both`, `petro_pass_ca`, etc.
//
//   `ta_petro` maps to `esso` (invoice/read-layer Esso identity
//   duality — never expose `ta_petro` in an operator-facing UI).
//
//   Returned array is deduplicated and emitted in the strict canonical
//   order `["esso", "pipeline", "petro_pass"]`, filtered to only those
//   networks actually authorized for the given customer profile.
//
// USAGE:
//   Admin Invoices.jsx `+Generate Invoice` Network dropdown consumes
//   this helper to hide unauthorized-network options per customer.
//   Backend `customer_id` filter remains authoritative for cross-
//   customer transaction isolation; this helper is UI selector
//   authorization only.
export function deriveAuthorizedInvoiceNetworks(customerProfile) {
  const out = new Set();
  const priceReport = (customerProfile && customerProfile.price_report_networks) || [];
  const lanes = (customerProfile && customerProfile.authorized_lanes) || [];
  const _canonical = (raw) => {
    const s = String(raw || "").toLowerCase().trim();
    if (s === "esso" || s.startsWith("esso_")) return "esso";
    if (s === "ta_petro") return "esso"; // preserve Esso-USA read-layer duality — never expose ta_petro
    if (s === "pipeline" || s.startsWith("pipeline_")) return "pipeline";
    if (s === "petro_pass" || s.startsWith("petro_pass_")) return "petro_pass";
    return "";
  };
  priceReport.forEach((n) => {
    const c = _canonical(n);
    if (c) out.add(c);
  });
  lanes.forEach((l) => {
    // authorized_lanes may be objects `{network, country}` OR compound strings
    const raw = l && typeof l === "object" ? l.network : l;
    const c = _canonical(raw);
    if (c) out.add(c);
  });
  return ["esso", "pipeline", "petro_pass"].filter((n) => out.has(n));
}
